import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { triguardBrandProfile } from '../../branding/companyBrand';
import BrandedPreviewShell from '../previews/BrandedPreviewShell';
import { CXReferral, getCXReferralAccess } from '../../services/cxReferralService';

const sampleActivity = [
  { detail: 'Quarterly pest plan', name: 'Megan Reed', status: 'Service completed', tone: 'success' },
  { detail: 'Termite inspection', name: 'Tyler Smith', status: 'Estimate sent', tone: 'warning' },
  { detail: 'General pest control', name: 'Jordan Patel', status: 'Form submitted', tone: 'info' },
];

const ReferralActivityPreview: React.FC = () => {
  const [liveReferral, setLiveReferral] = useState<CXReferral | null>(null);
  useEffect(() => {
    const token = localStorage.getItem('cxReferralAccessToken');
    if (token) getCXReferralAccess(token).then(setLiveReferral).catch(() => setLiveReferral(null));
  }, []);

  const activity = useMemo(() => {
    if (!liveReferral) return sampleActivity;
    const labels: Record<CXReferral['status'], string> = { booked: 'Service booked', cancelled: 'Cancelled', claimed: 'Form submitted', invited: 'Link created', opened: 'Link opened', paid: 'Reward paid', qualified: 'Reward earned' };
    return [{ detail: liveReferral.status === 'claimed' ? 'Service request received' : 'Referred through your link', name: liveReferral.referredName, status: labels[liveReferral.status], tone: ['booked', 'qualified', 'paid'].includes(liveReferral.status) ? 'success' : liveReferral.status === 'cancelled' ? 'default' : 'info' }];
  }, [liveReferral]);

  return (
    <BrandedPreviewShell brand={triguardBrandProfile} previewLabel="Referral activity" statusLabel="Activity">
      <Stack spacing={2}>
        <Button component={RouterLink} startIcon={<ArrowBackRoundedIcon />} sx={{ alignSelf: 'flex-start', color: 'var(--cx-brand-text)' }} to="/preview/referral-dashboard" variant="text">Back to rewards</Button>
        <Stack spacing={0.5}>
          <Typography variant="h4" sx={{ color: 'var(--cx-brand-text)', fontSize: { xs: 28, sm: 32 } }}>Referral activity</Typography>
          <Typography variant="body2" sx={{ color: 'var(--cx-brand-text-muted)' }}>Forms submitted from your referral link and where each person stands.</Typography>
        </Stack>
        <Stack spacing={1.25}>
          {activity.map((item) => {
            const initials = item.name.split(/\s+/).map((part) => part[0]).join('').slice(0, 2).toUpperCase();
            return (
              <Card key={`${item.name}-${item.status}`} sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'var(--cx-brand-primary-soft)', color: 'var(--cx-brand-primary-dark)', fontSize: 12, fontWeight: 900, height: 42, width: 42 }}>{initials}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography noWrap variant="body2" sx={{ color: 'var(--cx-brand-text)', fontWeight: 800 }}>{item.name}</Typography>
                    <Typography noWrap variant="caption" sx={{ color: 'var(--cx-brand-text-muted)' }}>{item.detail}</Typography>
                  </Box>
                  <Chip color={item.tone as 'success' | 'warning' | 'info' | 'default'} label={item.status} size="small" sx={{ fontWeight: 800 }} />
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </BrandedPreviewShell>
  );
};

export default ReferralActivityPreview;
